import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, ChevronLeft, PencilIcon, ShieldCheck } from 'lucide-react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { DetailItem } from '@/components/common/DetailItem';
import { NoDataFound } from '@/components/common/NoDataFound';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateTime, ResourceLabelMap } from '@/utility/helper';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { RoutesLink } from '@/utility/RouteLinks';
import { useActiveRole } from '@/hooks/useActiveRole';
import { IRole } from '@/interfaces/Role/role.interface';

export const ViewRole: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const { canEdit } = usePermissions();

    const [role, setRole] = useState<IRole | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        (async () => {
            setLoading(true);
            try {
                const res = await callApi<any>(
                    `${API_URL}${API_ENDPOINT.roles}/${id}`,
                    { method: 'GET' }
                );
                setRole(res.data || res);
            } catch (error) {
                ThrowError(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    if (!role) {
        return (
            <Layout>
                <NoDataFound
                    title="Role Not Found"
                    description="This role does not exist or has been deleted."
                    onAction={() => navigate(-1)}
                />
            </Layout>
        );
    }

    // Group permissions by resource for display
    const permissionMap: Record<string, string[]> = {};
    role.permissions.forEach((p) => {
        if (!permissionMap[p.resource]) permissionMap[p.resource] = [];
        
        let actionList: string[] = [];
        try {
            actionList = typeof p.actions === 'string' ? JSON.parse(p.actions) : p.actions;
        } catch {
            actionList = [p.actions as unknown as string];
        }

        if (Array.isArray(actionList)) {
            actionList.forEach((act) => {
                if (!permissionMap[p.resource].includes(act)) {
                    permissionMap[p.resource].push(act);
                }
            });
        }
    });

    const actionBadgeColor: Record<string, string> = {
        view: 'bg-blue-50 text-blue-700 border-blue-200',
        create: 'bg-green-50 text-green-700 border-green-200',
        update: 'bg-amber-50 text-amber-700 border-amber-200',
        delete: 'bg-red-50 text-red-700 border-red-200',
        add: 'bg-green-50 text-green-700 border-green-200',
        remove: 'bg-red-50 text-red-700 border-red-200',
    };

    return (
        <Layout>
            <div className="space-y-8 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-500">

                <MainHeading
                    title={<span className="capitalize">{role.name}</span>}
                    description={`${role?.permissions?.length ?? 0} permission${(role?.permissions?.length ?? 0) !== 1 ? 's' : ''} assigned`}
                    showBack
                    onBack={() => navigate(-1)}
                >
                    {canEdit(RESOURCES.ROLE) && (
                        <Button
                            variant="outline"
                            leftIcon={<PencilIcon className="w-4 h-4" />}
                            onClick={() => navigate(prefix + RoutesLink.EditRole.replace(':id', role.id))}
                        >
                            Edit Role
                        </Button>
                    )}
                </MainHeading>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                    {/* Left — Permissions matrix */}
                    <div className="lg:col-span-2">
                        <SectionCard title="Permission Matrix" icon={<ShieldCheck />}>
                            {Object.keys(permissionMap).length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4">No permissions assigned to this role.</p>
                            ) : (
                                <div className="divide-y divide-border/40">
                                    {Object.entries(permissionMap).map(([resource, actions]) => (
                                        <div key={resource} className="flex items-center gap-6 py-3 first:pt-0 last:pb-0">
                                            <div className="w-36 shrink-0">
                                                <span className="text-sm font-semibold text-foreground capitalize">
                                                    {ResourceLabelMap[resource] || resource}
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {actions.map((action) => (
                                                    <span
                                                        key={action}
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border capitalize ${actionBadgeColor[action] || 'bg-muted text-muted-foreground border-border'}`}
                                                    >
                                                        {action}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Right — Role info */}
                    <div className="space-y-6 sticky top-6">
                        <SectionCard title="Role Details" icon={<ShieldCheck />}>
                            <div className="space-y-5">
                                <DetailItem
                                    label="Role Name"
                                    value={<span className="capitalize">{role.name}</span>}
                                    icon={<ShieldCheck className="w-4 h-4" />}
                                />
                                <DetailItem
                                    label="Created"
                                    value={formatDateTime(role.createdAt)}
                                    icon={<Calendar className="w-4 h-4" />}
                                />
                                <DetailItem
                                    label="Last Updated"
                                    value={formatDateTime(role.updatedAt)}
                                    icon={<Calendar className="w-4 h-4" />}
                                />
                                <div className="pt-3 border-t border-border/40">
                                    <p className="text-xs text-muted-foreground mb-1">Total Permissions</p>
                                    <p className="text-2xl font-bold text-foreground">{role.permissions.length}</p>
                                </div>
                            </div>
                        </SectionCard>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
